/**
 * Skeleton component for MenuScope loading state
 * Provides a complete loading placeholder for the entire MenuScope page
 *
 * @module containers/MenuScope/MenuScopeSkeleton
 */

// MUI Components
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import { useTheme } from '@mui/material/styles';

// Components
import { ScopeCardSkeleton } from 'components/ScopeCard';

// Styles
import useStyles from './styles';

/**
 * Menu Scope Skeleton Component
 * Renders a complete skeleton placeholder for the MenuScope page
 * including header (title + description) and scope cards
 *
 * @returns {React.ReactElement} Complete MenuScope skeleton
 *
 * @example
 * <MenuScopeSkeleton />
 */
const MenuScopeSkeleton = (): React.ReactElement => {
  const classes = useStyles();
  const theme = useTheme();

  return (
    <Box className={classes.container}>
      {/* Header skeleton */}
      <Box className={classes.headerSection}>
        {/* Title skeleton - mucho más grande para que sea visible */}
        <Skeleton
          height={theme.typography.pxToRem(60)}
          sx={{
            bgcolor: 'grey.400',
            mb: 3,
            borderRadius: 2,
            margin: '0 auto',
            transform: 'scale(1.0)',
          }}
          variant="text"
          width={500}
        />
        {/* Subtitle skeleton - líneas más grandes y visibles */}
        <Skeleton
          height={theme.typography.pxToRem(30)}
          sx={{
            bgcolor: 'grey.400',
            mb: 1.5,
            borderRadius: 2,
            margin: '0 auto',
          }}
          variant="text"
          width={600}
        />
        <Skeleton
          height={theme.typography.pxToRem(30)}
          sx={{
            bgcolor: 'grey.400',
            borderRadius: 2,
            margin: '0 auto',
          }}
          variant="text"
          width={550}
        />
      </Box>

      {/* Cards skeleton - usando ScopeCardSkeleton */}
      <Box className={classes.cardsContainer}>
        <Box className={classes.cardWrapper}>
          <ScopeCardSkeleton />
        </Box>
        <Box className={classes.cardWrapper}>
          <ScopeCardSkeleton />
        </Box>
      </Box>
    </Box>
  );
};

export default MenuScopeSkeleton;
